import React, { useState, useEffect, useCallback } from "react";
import { BiMenuAltLeft } from "react-icons/bi";
import logo from "../assets/img/logopng.png";
import {
  FiBriefcase,
  FiDollarSign,
  FiUsers,
  FiStar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiTrendingUp,
  FiTrendingDown,
  FiEye,
  FiEdit,
  FiTrash2,
  FiMessageSquare,
  FiFileText,
  FiCreditCard,
  FiMapPin,
  FiGlobe,
  FiCalendar,
  FiPercent,
  FiBarChart2,
  FiShield,
  FiRefreshCw,
  FiChevronDown,
  FiChevronUp,
  FiHash,
  FiActivity,
  FiShoppingCart,
  FiCreditCard as FiCreditCardIcon,
  FiPlus,
} from "react-icons/fi";
import { useNavigate, useParams } from "react-router-dom";
import { BASE_URL } from "src/Config/api.config";
import useLocalStorage from "src/Hooks/useLocalStorage";
import { menuItems } from "src/Pages/AdminDashboard";
import { IoMdLogOut } from "react-icons/io";
import toast from "react-hot-toast";

const EmployerProfile = ({ employerId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [notePriority, setNotePriority] = useState("medium");
  const [noteType, setNoteType] = useState("general");
  const params = useParams();
  const [selectedTab, setSelectedTab] = useLocalStorage(
    "adminSelectedTab",
    "dashboard"
  );
  const [adminData, setAdminData] = useLocalStorage("adminData", "");
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(true);
  // Add these states near your other useState declarations
  const [showAddFlexicoinsModal, setShowAddFlexicoinsModal] = useState(false);
  const [flexicoinsAmount, setFlexicoinsAmount] = useState("");
  const [flexicoinsDescription, setFlexicoinsDescription] = useState("");
  const [addingFlexicoins, setAddingFlexicoins] = useState(false);

  // Add this handler for adjusting flexicoins (negative amounts)
  // Update your handleAdjustFlexicoins function in React
  const handleAdjustFlexicoins = async (isPositive = true) => {
    if (!flexicoinsAmount || !flexicoinsDescription) {
      toast.error("Please enter amount and description");
      return;
    }

    const amount = parseInt(flexicoinsAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid positive amount");
      return;
    }

    // CRITICAL: Check if deduction would result in negative balance
    if (!isPositive) {
      const currentBalance = basic_info?.[0].flexicoins || 0;
      if (amount > currentBalance) {
        toast.error(
          `Cannot deduct ${amount} flexicoins. Current balance is only ${currentBalance} FC.`
        );
        return;
      }
    }

    try {
      setAddingFlexicoins(true);
      const result = await handleAdminAction(
        isPositive ? "add_flexicoins" : "adjust_flexicoins",
        {
          amount: isPositive ? amount : -amount, // Send negative amount for deduction
          description: flexicoinsDescription,
          service_type: "admin_adjustment",
        }
      );

      if (result) {
        toast.success(
          `${
            isPositive ? "Added" : "Deducted"
          } ${amount} flexicoins successfully!`
        );
        setShowAddFlexicoinsModal(false);
        setFlexicoinsAmount("");
        setFlexicoinsDescription("");
        fetchEmployerProfile();
      }
    } catch (error) {
      console.error("Error adjusting flexicoins:", error);

      // Handle specific error messages
      if (error.message && error.message.includes("negative balance")) {
        const currentBalance = basic_info?.[0].flexicoins || 0;
        toast.error(
          `Cannot deduct ${amount} flexicoins. Would result in negative balance. Current balance: ${currentBalance} FC`
        );
      } else {
        toast.error(
          "Failed to adjust flexicoins: " + (error.message || "Unknown error")
        );
      }
    } finally {
      setAddingFlexicoins(false);
    }
  };
  const handleAddFlexicoins = async () => {
    if (!flexicoinsAmount || !flexicoinsDescription) {
      toast.error("Please enter amount and description");
      return;
    }

    const amount = parseInt(flexicoinsAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid positive amount");
      return;
    }

    try {
      setAddingFlexicoins(true);
      const result = await handleAdminAction("add_flexicoins", {
        amount: amount,
        description: flexicoinsDescription,
        service_type: "admin_adjustment",
      });

      if (result) {
        toast.success(`${amount} flexicoins added successfully!`);
        setShowAddFlexicoinsModal(false);
        setFlexicoinsAmount("");
        setFlexicoinsDescription("");
        // Refresh profile data
        fetchEmployerProfile();
      }
    } catch (error) {
      console.error("Error adding flexicoins:", error);
      toast.error("Failed to add flexicoins: " + error.message);
    } finally {
      setAddingFlexicoins(false);
    }
  };

  // Fetch employer profile data
  const fetchEmployerProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${BASE_URL}adm.profile.employer?client_id=${employerId}`,
        {
          headers: {
            Authorization: `Bearer ${adminData.uid}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === "Employer profile retrieved successfully") {
        setProfileData(data.result);
      } else {
        throw new Error(data.message || "Failed to fetch employer profile");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching employer profile:", err);
    } finally {
      setLoading(false);
    }
  }, [employerId]);

  // Handle admin actions
  const handleAdminAction = async (action, payload) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(`${BASE_URL}adm.profile.employer`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${adminData.uid}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          action,
          client_id: employerId,
          ...payload,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.status === "Employer profile retrieved successfully") {
        // Refresh profile data
        fetchEmployerProfile();
        return data;
      } else if (data.status === "Note added successfully") {
        fetchEmployerProfile();
        toast.success("Note added!");
      } else if (data.status === "Employer status updated successfully") {
        fetchEmployerProfile();
        toast.success("Employer status updated!");
      } else if (data.status === "Employer verification updated successfully") {
        fetchEmployerProfile();
        toast.success("Employer verified status updated!");
      } else if (
        data.status === "Flexicoins added successfully to employer account"
      ) {
        toast.success(`${data.result.amount_added} Flexicoins added!`);
        fetchEmployerProfile();
      } else if (data.status === "Flexicoins balance adjusted successfully") {
        toast.success(`${data.result.amount_adjusted} Flexicoins `);
      } else {
        throw new Error(data.message || "Action failed");
      }
    } catch (err) {
      console.error("Error performing admin action:", err);
      alert(`Error: ${err.message}`);
    }
  };

  // Add admin note
  const handleAddNote = async () => {
    if (!newNote.trim()) {
      alert("Please enter a note");
      return;
    }

    const result = await handleAdminAction("add_note", {
      note: newNote,
      priority: notePriority,
      type: noteType,
    });

    if (result) {
      setNewNote("");
      setShowNotesForm(false);
    }
  };

  // Update employer status
  const handleStatusToggle = async (newStatus) => {
    await handleAdminAction("update_status", {
      status: newStatus,
    });
  };

  // Update verification status
  const handleVerificationToggle = async (verified) => {
    await handleAdminAction("update_verification", {
      verified: verified === "1" ? "0" : "1",
    });
  };

  const formData = new FormData();
  formData.append("id", adminData.id);

  const logout = async () => {
    const response = await fetch(`${BASE_URL}logout`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${adminData.uid}`,
      },
      body: formData,
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "ok") {
          setAdminData(null);
          navigate("/");
        } else {
          navigate("/");
        }
      })
      .catch((err) => console.log(err));

    return response;
  };

  // Initial fetch
  useEffect(() => {
    fetchEmployerProfile();
  }, [fetchEmployerProfile]);

  // Helper functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: profileData?.wallet?.currency || "USD",
    }).format(amount);
  };

  const getRiskColor = (score) => {
    if (score >= 80) return "text-green-600 bg-green-50";
    if (score >= 60) return "text-yellow-600 bg-yellow-50";
    return "text-red-600 bg-red-50";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "text-green-600 bg-green-50";
      case "inactive":
        return "text-red-600 bg-red-50";
      case "suspended":
        return "text-orange-600 bg-orange-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const renderStars = (rating) => {
    const fullStars = Math.floor(rating);
    const halfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center">
        {[...Array(5)].map((_, i) => (
          <span
            key={i}
            className={`text-lg ${
              i < fullStars
                ? "text-yellow-400"
                : i === fullStars && halfStar
                ? "text-yellow-300"
                : "text-gray-300"
            }`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-600">
          ({rating?.toFixed(1)})
        </span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Calculate derived statistics from flexicoins data
  const calculateStatistics = () => {
    if (!profileData) return {};

    const flexicoins = profileData.flexicoins;
    const stats = {
      total_spent: flexicoins?.statistics?.total_spent || 0,
      total_transactions: flexicoins?.statistics?.total_transactions || 0,
      total_purchases: flexicoins?.statistics?.total_purchases || 0,
      net_flexicoins: flexicoins?.statistics?.net_flexicoins || 0,
    };

    return stats;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading employer profile...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="h-12 w-12 text-red-500 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Error Loading Profile
          </h3>
          <p className="mt-2 text-sm text-gray-600">{error}</p>
          <button
            onClick={fetchEmployerProfile}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
          >
            <FiRefreshCw className="mr-2 h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // If no profile data
  if (!profileData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiBriefcase className="h-12 w-12 text-gray-400 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            No Profile Found
          </h3>
          <p className="mt-2 text-sm text-gray-600">
            The employer profile could not be loaded.
          </p>
        </div>
      </div>
    );
  }

  const {
    basic_info,
    wallet,
    flexicoins,
    statistics,
    job_postings,
    connections,
    reviews,
    payments,
    company_info,
    verification,
    admin,
    metadata,
    summary,
  } = profileData;

  const basicInfo = basic_info?.[0];
  const stats = calculateStatistics();

  return (
    <>
      {/* Main Content Area */}
      <div className="flex-1 overflow-auto bg-gray-50">
        {/* Header Section */}
        <div className="mb-6 flex flex-col p-6">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <h1 className="text-2xl font-bold text-gray-900">
                  Employer Profile
                </h1>
              </div>
              <p className="text-gray-600 mt-1">
                Manage employer account and view detailed information
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={fetchEmployerProfile}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <FiRefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() =>
                    handleStatusToggle(basicInfo?.active === "1" ? "0" : "1")
                  }
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
                    basicInfo?.active === "1"
                      ? "bg-red-100 text-red-700 hover:bg-red-200"
                      : "bg-green-100 text-green-700 hover:bg-green-200"
                  }`}
                >
                  {basicInfo?.active === "1" ? (
                    <>
                      <FiXCircle className="mr-2 h-4 w-4" />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Activate
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleVerificationToggle(basicInfo?.validated)}
                  className={`inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium ${
                    !basicInfo?.validated
                      ? "bg-yellow-100 text-yellow-700 hover:bg-yellow-200"
                      : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                  }`}
                >
                  {!basicInfo?.validated ? (
                    <>
                      <FiAlertCircle className="mr-2 h-4 w-4" />
                      Unverify
                    </>
                  ) : (
                    <>
                      <FiCheckCircle className="mr-2 h-4 w-4" />
                      Verify
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Profile Header Card */}
        <div className="px-6 mb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Profile Image & Basic Info */}
              <div className="flex items-start gap-4">
                <div className="relative">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                    {basicInfo?.company_logo ? (
                      <img
                        src={`${BASE_URL}${basicInfo.company_logo}`}
                        alt="Company Logo"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-blue-600">
                        {basicInfo?.first_name?.charAt(0) || "E"}
                        {basicInfo?.last_name?.charAt(0) || "M"}
                      </span>
                    )}
                  </div>
                  {basicInfo?.validated && (
                    <div className="absolute -bottom-1 -right-1 bg-green-500 text-white p-1 rounded-full">
                      <FiCheckCircle className="w-4 h-4" />
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="text-xl font-bold text-gray-900">
                    {basicInfo?.first_name} {basicInfo?.last_name}
                  </h2>
                  <p className="text-gray-600">{basicInfo?.email}</p>
                  {basicInfo?.company_name && (
                    <p className="text-sm text-gray-500 mt-1">
                      {basicInfo.company_name}
                    </p>
                  )}

                  <div className="flex flex-wrap gap-2 mt-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                        basicInfo?.active === "1" ? "active" : "inactive"
                      )}`}
                    >
                      {basicInfo?.active === "1" ? "Active" : "Inactive"}
                    </span>

                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        basicInfo?.validated
                          ? "text-green-600 bg-green-50"
                          : "text-yellow-600 bg-yellow-50"
                      }`}
                    >
                      {basicInfo?.validated ? "Verified" : "Unverified"}
                    </span>

                    {basicInfo?.validated === "1" && (
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-green-600 bg-green-50">
                        <FiCheckCircle className="mr-1 h-3 w-3" />
                        Validated
                      </span>
                    )}

                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-blue-600 bg-blue-50">
                      <FiBriefcase className="mr-1 h-3 w-3" />
                      Employer
                    </span>
                  </div>
                </div>
              </div>

              {/* Wallet & Stats Summary */}
              <div className="md:ml-auto grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiDollarSign className="h-5 w-5 text-blue-600" />
                    <span className="ml-2 text-sm text-gray-600">Wallet</span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {formatCurrency(wallet?.balance || 0)}
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiShoppingCart className="h-5 w-5 text-green-600" />
                    <span className="ml-2 text-sm text-gray-600">
                      Flexicoins
                    </span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {basic_info?.[0].flexicoins || 0}
                  </p>
                </div>

                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiCreditCardIcon className="h-5 w-5 text-yellow-600" />
                    <span className="ml-2 text-sm text-gray-600">
                      Total Spent
                    </span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {stats.total_spent}
                  </p>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-center">
                    <FiHash className="h-5 w-5 text-purple-600" />
                    <span className="ml-2 text-sm text-gray-600">
                      Transactions
                    </span>
                  </div>
                  <p className="text-xl font-bold text-gray-900 mt-2">
                    {stats.total_transactions}
                  </p>
                </div>
              </div>
            </div>

            {/* Company Info Row */}
            {basicInfo?.company_name && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {basicInfo.company_name}
                    </h3>
                    <p className="text-gray-600">
                      {basicInfo.company_description}
                    </p>
                    {basicInfo.company_phone && (
                      <div className="flex items-center mt-1">
                        <svg
                          className="h-4 w-4 text-gray-400 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        <span className="text-sm text-gray-600">
                          {basicInfo.company_prefix_phone}{" "}
                          {basicInfo.company_phone}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">CIF</p>
                    <p className="font-semibold">{basicInfo.company_cif}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Address Info */}
            {basicInfo?.address_street && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex items-start">
                  <FiMapPin className="h-5 w-5 text-gray-400 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-900">
                      {basicInfo.address_street}
                    </p>
                    {basicInfo.city && (
                      <p className="text-sm text-gray-600">
                        {basicInfo.city}
                        {basicInfo.latitude && basicInfo.longitude && (
                          <span className="ml-2 text-xs text-gray-400">
                            ({basicInfo.latitude}, {basicInfo.longitude})
                          </span>
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="px-6 mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: "overview", label: "Overview", icon: FiBarChart2 },
                { id: "flexicoins", label: "Flexicoins", icon: FiActivity },
                { id: "jobs", label: "Job Postings", icon: FiBriefcase },
                {
                  id: "transactions",
                  label: "Transactions",
                  icon: FiCreditCard,
                },
                { id: "admin", label: "Admin", icon: FiShield },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm
                  ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }
                `}
                >
                  <tab.icon
                    className={`mr-2 h-5 w-5 ${
                      activeTab === tab.id ? "text-blue-500" : "text-gray-400"
                    }`}
                  />
                  {tab.label}
                  {tab.id === "admin" && admin?.notes?.length > 0 && (
                    <span className="ml-2 bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                      {admin.notes.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 pb-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div className="space-y-6">
                {/* Statistics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Total Jobs Posted
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {basicInfo?.jobs || 0}
                        </p>
                      </div>
                      <FiBriefcase className="h-8 w-8 text-blue-500" />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Total Purchased</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {flexicoins?.total_purchased || 0}
                        </p>
                      </div>
                      <FiShoppingCart className="h-8 w-8 text-green-500" />
                    </div>
                    {flexicoins?.last_purchase_date_formatted && (
                      <div className="mt-2 text-sm text-gray-500">
                        Last: {flexicoins.last_purchase_date_formatted}
                      </div>
                    )}
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Total Spent</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {stats.total_spent}
                        </p>
                      </div>
                      <FiDollarSign className="h-8 w-8 text-yellow-500" />
                    </div>
                    <div className="mt-2 text-sm text-gray-500">
                      Net: {stats.net_flexicoins} flexicoins
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">
                          Verification Level
                        </p>
                        <p className="text-2xl font-bold text-gray-900">
                          {verification?.level || 0}/4
                        </p>
                      </div>
                      <FiPercent className="h-8 w-8 text-purple-500" />
                    </div>
                    <div className="mt-2">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(verification?.level || 0) * 25}%`,
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Details */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Account Information
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-600">Email Status</p>
                          <p className="font-medium">
                            {basicInfo?.validated === "1"
                              ? "Verified"
                              : "Unverified"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">2FA Status</p>
                          <p className="font-medium">
                            {basicInfo?.two_fa === "1" ? "Enabled" : "Disabled"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Newsletter</p>
                          <p className="font-medium">
                            {basicInfo?.is_subscribed === "1"
                              ? "Subscribed"
                              : "Not Subscribed"}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Gift Received</p>
                          <p className="font-medium">
                            {basicInfo?.gift_received === "1" ? "Yes" : "No"}
                          </p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Last Data Update
                        </p>
                        <p className="font-medium">
                          {basicInfo?.data_updated
                            ? formatDate(basicInfo.data_updated)
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Company Verification Status */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Verification Status
                    </h3>
                    <div className="space-y-3">
                      {[
                        {
                          key: "email_verified",
                          label: "Email Verified",
                          value: basicInfo?.validated === "1",
                        },
                        {
                          key: "company_verified",
                          label: "Company Verified",
                          value: basicInfo?.company_validated === "1",
                        },
                        {
                          key: "verification_approved",
                          label: "Verification Approved",
                          value: basicInfo?.validated || false,
                        },
                      ].map((item) => (
                        <div
                          key={item.key}
                          className="flex items-center justify-between p-3 border rounded-lg"
                        >
                          <div className="flex items-center">
                            <FiCheckCircle
                              className={`h-5 w-5 mr-3 ${
                                item.value ? "text-green-500" : "text-gray-300"
                              }`}
                            />
                            <span className="text-sm">{item.label}</span>
                          </div>
                          {item.value ? (
                            <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                              Verified
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                              Pending
                            </span>
                          )}
                        </div>
                      ))}
                      {basicInfo?.verification_session_id && (
                        <div className="p-3 border rounded-lg">
                          <p className="text-sm text-gray-600">
                            Verification Session ID
                          </p>
                          <p className="text-xs font-mono text-gray-800 truncate">
                            {basicInfo.verification_session_id}
                          </p>
                          {basicInfo.verification_updated && (
                            <p className="text-xs text-gray-500 mt-1">
                              Updated:{" "}
                              {formatDate(basicInfo.verification_updated)}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary Information */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-900 mb-2">
                    Account Summary
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-blue-700">Total Balance</p>
                      <p className="text-lg font-bold text-blue-900">
                        {formatCurrency(summary?.combined_balance?.total || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">
                        Profile Completion
                      </p>
                      <p className="text-lg font-bold text-blue-900">
                        {metadata?.profile_completion || 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-blue-700">Total Value</p>
                      <p className="text-lg font-bold text-blue-900">
                        {formatCurrency(summary?.total_value || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Flexicoins Tab */}
            {activeTab === "flexicoins" && flexicoins && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Flexicoins Details
                  </h3>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Current Balance</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {basic_info?.[0].flexicoins}
                    </p>
                  </div>{" "}
                  <button
                    onClick={() => setShowAddFlexicoinsModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FiPlus className="mr-2 h-4 w-4" />
                    Add/Adjust Flexicoins
                  </button>
                </div>

                {/* Flexicoins Statistics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-gray-600">Total Purchased</p>
                    <p className="text-xl font-bold text-gray-900">
                      {flexicoins.total_purchased}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-gray-600">Net Flexicoins</p>
                    <p
                      className={`text-xl font-bold ${
                        flexicoins.statistics?.net_flexicoins < 0
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {flexicoins.statistics?.net_flexicoins || 0}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-gray-600">Last Purchase</p>
                    <p className="text-sm text-gray-900">
                      {flexicoins.last_purchase_date_formatted || "Never"}
                    </p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-gray-600">Balance Consistency</p>
                    <p
                      className={`text-sm font-medium ${
                        flexicoins.balance_consistency?.is_consistent
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {flexicoins.balance_consistency?.is_consistent
                        ? "Consistent"
                        : "Inconsistent"}
                    </p>
                  </div>
                </div>

                {/* Recent Transactions */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                    Recent Transactions ({flexicoins.recent_transactions_count})
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Service
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {flexicoins.recent_transactions?.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900">
                                {transaction.created_at_formatted}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                  transaction.is_positive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {transaction.type}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900">
                                {transaction.service_name}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900 max-w-xs">
                                {transaction.description}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <p
                                className={`font-medium ${
                                  transaction.is_positive
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {transaction.is_positive ? "+" : "-"}
                                {transaction.absolute_amount}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded ${
                                  transaction.synced_to_wallet
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {transaction.synced_to_wallet
                                  ? "Synced"
                                  : "Pending"}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {(!flexicoins.recent_transactions ||
                      flexicoins.recent_transactions.length === 0) && (
                      <div className="text-center py-12">
                        <FiActivity className="h-12 w-12 text-gray-400 mx-auto" />
                        <p className="mt-4 text-gray-500">
                          No transactions found
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Job Postings Tab */}
            {activeTab === "jobs" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Job Postings
                  </h3>
                  <span className="text-sm text-gray-600">
                    {job_postings?.length || 0} total
                  </span>
                </div>

                {job_postings && job_postings.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Job Title
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Budget per hour
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Status
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Created
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {job_postings.map((job) => (
                          <tr key={job.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {job.job_title}
                                </p>
                                <p className="text-sm text-gray-500 truncate max-w-xs">
                                  {job.job_description}
                                </p>
                              </div>
                            </td>
                            <td className="px-4 py-4">
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-800 rounded">
                                {job.salary_range}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`px-2 py-1 text-xs font-medium rounded-full ${
                                  job.status === "active"
                                    ? "bg-green-100 text-green-800"
                                    : job.status === "draft"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {job.status}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-500">
                                {job.created_at_formatted}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <button className="text-blue-600 hover:text-blue-900 text-sm font-medium">
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FiBriefcase className="h-12 w-12 text-gray-400 mx-auto" />
                    <p className="mt-4 text-gray-500">No job postings found</p>
                  </div>
                )}
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Transaction History
                  </h3>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Total Transactions</p>
                    <p className="text-xl font-bold text-gray-900">
                      {flexicoins?.statistics?.total_transactions || 0}
                    </p>
                  </div>
                </div>

                {flexicoins?.transaction_summary && (
                  <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm text-gray-600">
                            First Transaction
                          </p>
                          <p className="font-medium">
                            {
                              flexicoins.transaction_summary
                                .first_transaction_formatted
                            }
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">
                            Last Transaction
                          </p>
                          <p className="font-medium">
                            {
                              flexicoins.transaction_summary
                                .last_transaction_formatted
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex justify-between">
                        <div>
                          <p className="text-sm text-gray-600">
                            Total Purchases
                          </p>
                          <p className="font-medium text-green-600">
                            {flexicoins.transaction_summary.total_purchases}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Spent</p>
                          <p className="font-medium text-red-600">
                            {flexicoins.transaction_summary.total_spent}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Total Refunds</p>
                          <p className="font-medium text-blue-600">
                            {flexicoins.transaction_summary.total_refunds}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {flexicoins?.recent_transactions &&
                flexicoins.recent_transactions.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Date
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Type
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Description
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Amount
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            Reference
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {flexicoins.recent_transactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-gray-50">
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900">
                                {transaction.created_at_formatted}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
                                  transaction.is_positive
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }`}
                              >
                                {transaction.type}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-900">
                                {transaction.description}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <p
                                className={`font-medium ${
                                  transaction.is_positive
                                    ? "text-green-600"
                                    : "text-red-600"
                                }`}
                              >
                                {transaction.is_positive ? "+" : "-"}
                                {transaction.absolute_amount}
                              </p>
                            </td>
                            <td className="px-4 py-4">
                              <p className="text-sm text-gray-500 font-mono">
                                {transaction.reference_id}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FiCreditCard className="h-12 w-12 text-gray-400 mx-auto" />
                    <p className="mt-4 text-gray-500">
                      No transaction history found
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Admin Tab */}
            {activeTab === "admin" && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Admin Management
                  </h3>
                  <button
                    onClick={() => setShowNotesForm(!showNotesForm)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <FiMessageSquare className="mr-2 h-4 w-4" />
                    Add Note
                  </button>
                </div>

                {/* Add Note Form */}
                {showNotesForm && (
                  <div className="mb-6 p-4 border rounded-lg bg-gray-50">
                    <h4 className="font-medium text-gray-900 mb-3">
                      Add Admin Note
                    </h4>
                    <div className="space-y-4">
                      <div>
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Enter note details..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          rows="3"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Priority
                          </label>
                          <select
                            value={notePriority}
                            onChange={(e) => setNotePriority(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Type
                          </label>
                          <select
                            value={noteType}
                            onChange={(e) => setNoteType(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="general">General</option>
                            <option value="warning">Warning</option>
                            <option value="issue">Issue</option>
                            <option value="follow_up">Follow-up</option>
                            <option value="verification">Verification</option>
                          </select>
                        </div>
                      </div>

                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setShowNotesForm(false)}
                          className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddNote}
                          className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                        >
                          Add Note
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Admin Notes */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                    Admin Notes ({admin?.notes?.length || 0})
                  </h4>

                  {admin?.notes && admin.notes.length > 0 ? (
                    <div className="space-y-4">
                      {admin.notes.map((note) => (
                        <div key={note.id} className="border rounded-lg p-4">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-medium text-gray-900">
                                {note.note}
                              </p>
                              <p className="text-sm text-gray-500 mt-1">
                                Added on {note.created_at_formatted} by Admin #
                                {note.admin_id}
                              </p>
                            </div>
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded ${
                                note.priority === "high" ||
                                note.priority === "critical"
                                  ? "bg-red-100 text-red-800"
                                  : note.priority === "medium"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : "bg-green-100 text-green-800"
                              }`}
                            >
                              {note.priority}
                            </span>
                          </div>
                          {note.updated_at_formatted && (
                            <p className="text-xs text-gray-400">
                              Updated: {note.updated_at_formatted}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FiMessageSquare className="h-12 w-12 text-gray-400 mx-auto" />
                      <p className="mt-4 text-gray-500">No admin notes yet</p>
                    </div>
                  )}
                </div>

                {/* Account Actions */}
                <div className="mt-8 pt-8 border-t">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                    Account Actions
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <button
                      onClick={() =>
                        handleStatusToggle(
                          basicInfo?.active === "1" ? "0" : "1"
                        )
                      }
                      className={`p-4 border rounded-lg text-left ${
                        basicInfo?.active === "1"
                          ? "border-red-200 bg-red-50 hover:bg-red-100"
                          : "border-green-200 bg-green-50 hover:bg-green-100"
                      }`}
                    >
                      <div className="flex items-center">
                        {basicInfo?.active === "1" ? (
                          <FiXCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <FiCheckCircle className="h-5 w-5 text-green-600" />
                        )}
                        <span className="ml-3 font-medium">
                          {basicInfo?.active === "1"
                            ? "Deactivate Account"
                            : "Activate Account"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {basicInfo?.active === "1"
                          ? "Temporarily disable this employer's account"
                          : "Re-activate this employer's account"}
                      </p>
                    </button>

                    <button
                      onClick={() =>
                        handleVerificationToggle(basicInfo?.validated)
                      }
                      className={`p-4 border rounded-lg text-left ${
                        basicInfo?.validated
                          ? "border-yellow-200 bg-yellow-50 hover:bg-yellow-100"
                          : "border-blue-200 bg-blue-50 hover:bg-blue-100"
                      }`}
                    >
                      <div className="flex items-center">
                        {basicInfo?.validated ? (
                          <FiAlertCircle className="h-5 w-5 text-yellow-600" />
                        ) : (
                          <FiCheckCircle className="h-5 w-5 text-blue-600" />
                        )}
                        <span className="ml-3 font-medium">
                          {basicInfo?.validated
                            ? "Remove Verification"
                            : "Verify Account"}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        {basicInfo?.validated
                          ? "Remove verified status from this employer"
                          : "Mark this employer as verified"}
                      </p>
                    </button>

                    <button className="p-4 border border-gray-200 rounded-lg text-left hover:bg-gray-50">
                      <div className="flex items-center">
                        <FiTrash2 className="h-5 w-5 text-gray-600" />
                        <span className="ml-3 font-medium">Delete Account</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">
                        Permanently delete this employer account and all
                        associated data
                      </p>
                    </button>
                  </div>
                </div>

                {/* Account Metadata */}
                <div className="mt-8 pt-8 border-t">
                  <h4 className="text-sm font-medium text-gray-500 mb-4">
                    Account Metadata
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-gray-600">
                        Profile Completion
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        {metadata?.profile_completion || 0}%
                      </p>
                    </div>

                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-gray-600">Last Updated</p>
                      <p className="text-lg font-medium text-gray-900">
                        {admin?.last_updated || "N/A"}
                      </p>
                    </div>

                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-gray-600">Total Value</p>
                      <p className="text-lg font-medium text-gray-900">
                        {formatCurrency(summary?.total_value || 0)}
                      </p>
                    </div>

                    <div className="p-3 border rounded-lg">
                      <p className="text-sm text-gray-600">Combined Balance</p>
                      <p className="text-lg font-medium text-gray-900">
                        {formatCurrency(summary?.combined_balance?.total || 0)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Add Flexicoins Modal */}
      {showAddFlexicoinsModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  Add/Adjust Flexicoins
                </h3>
                <button
                  onClick={() => setShowAddFlexicoinsModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      value={flexicoinsAmount}
                      onChange={(e) => setFlexicoinsAmount(e.target.value)}
                      placeholder="Enter amount"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                      <span className="text-gray-500">FC</span>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={flexicoinsDescription}
                    onChange={(e) => setFlexicoinsDescription(e.target.value)}
                    placeholder="Enter reason for adjustment..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Balance
                  </label>
                  <div className="p-3 bg-gray-50 rounded border">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Employer:</span>
                      <span className="font-semibold">
                        {basic_info?.[0].flexicoins || 0} FC
                      </span>
                    </div>
                    {flexicoinsAmount && (
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-600">New Balance:</span>
                        <span
                          className={`font-semibold ${
                            parseInt(flexicoinsAmount) > 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {(basic_info?.[0].flexicoins || 0) +
                            parseInt(flexicoinsAmount || 0)}{" "}
                          FC
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => handleAdjustFlexicoins(false)}
                  disabled={
                    addingFlexicoins ||
                    !flexicoinsAmount ||
                    !flexicoinsDescription ||
                    (parseInt(flexicoinsAmount) || 0) >
                      (basic_info?.[0].flexicoins || 0)
                  }
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingFlexicoins ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Deducting...
                    </>
                  ) : (
                    "Deduct Flexicoins"
                  )}
                </button>
                {flexicoinsAmount &&
                  parseInt(flexicoinsAmount) >
                    (basic_info?.[0].flexicoins || 0) && (
                    <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                      ⚠️ Cannot deduct {flexicoinsAmount} FC. Exceeds current
                      balance of {basic_info?.[0].flexicoins || 0} FC.
                    </div>
                  )}
                <button
                  onClick={() => handleAdjustFlexicoins(true)}
                  disabled={
                    addingFlexicoins ||
                    !flexicoinsAmount ||
                    !flexicoinsDescription
                  }
                  className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addingFlexicoins ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Adding...
                    </>
                  ) : (
                    "Add Flexicoins"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployerProfile;
